<template>
    <slot v-bind="{
        ...state,
        setPerformanceSelectorPopover,
        setPackageDialog,
        handlePromoCodeClick,
        handlePromoCodeClose,
        handlePromoCodeInput,
        handlePromoCodeSubmit,
    }" />
</template>

<script setup>
import { onBeforeMount, onBeforeUnmount, nextTick, reactive } from 'vue';
import { get } from 'lodash';

import zonedDate from '../utils/zoned-date';
import { buttonStates, getBookingState } from '../services/booking';
import { getUpcomingInstances } from '../services/inventory';
import performanceSelectorPopover from '../utils/performance-selector-popover';
import { getPromoCode } from '../utils/cookies';
import { applyPromoCode, removePromoCode } from '../services/blocksoffice';

const props = defineProps({
    startDate: {
        type: String,
    },
    performanceId: {
        type: Number,
        default: null,
    },
    seriesId: {
        type: Number,
        default: null,
    },
    cyoId: {
        type: Number,
        default: null,
    },
    useBestAvailable: {
        type: Boolean,
        default: false,
    },
    useProgressiveCyo: {
        type: Boolean,
        default: false,
    },
    bookingOverride: {
        type: Object,
        default: {},
    },
});

const state = reactive({
    isLoading: true,
    isProcessing: false,
    selectedInstance: {
        bookingState: {
            state: buttonStates.noButtons,
        },
    },
    promoCodeStatus: 'loading',
    promoCodeError: null,
    promoCode: null,
    showPromoCodeForm: false,
    inOverlay: false,
    isDesktop: false,
    teleportTo: '.event-header-sticky__actions',
});

let stickyHeaderObserver;
let packageDialog;

const setPerformanceSelectorPopover = element => {
    if (element) {
        performanceSelectorPopover(element);
    }
}

const setPackageDialog = element => {
    packageDialog = element;
}

const handlePromoCodeClick = () => {
    if (state.promoCodeStatus === 'pending') {
        state.showPromoCodeForm = true;
    } else if (state.promoCodeStatus === 'applied') {
        const prevStatus = state.promoCodeStatus;

        state.promoCodeStatus = 'removing';

        removePromoCode().then(() => {
            window.location.reload();
        }).catch(() => {
            state.promoCodeStatus = prevStatus;
        });
    }
}

const handlePromoCodeClose = () => {
    state.showPromoCodeForm = false;
}

const handlePromoCodeInput = event => {
    state.promoCode = event.target.value;
}

const handlePromoCodeSubmit = () => {
    const prevStatus = state.promoCodeStatus;

    state.promoCodeStatus = 'applying';

    applyPromoCode(state.promoCode).then(response => {
        if (response.status) {
            window.location.reload();
        } else {
            state.promoCodeError = response.message;
        }
    }).finally(() => {
        state.promoCodeStatus = prevStatus;
    })
}

onBeforeMount(async () => {
    state.promoCode = getPromoCode();

    state.promoCodeStatus = state.promoCode ? 'applied' : 'pending';

    if (props.startDate) {
        const startDate = zonedDate(props.startDate);

        let bookingState = {
            state: buttonStates.noLongerAvailable,
        }

        if (+startDate > +zonedDate()) {
            const bookingUrl = get(props.bookingOverride, 'bookingLinkURL');

            bookingState = await getBookingState({
                performanceId: props.performanceId,
                seriesId: props.seriesId,
                createYourOwnId: props.cyoId,
                useBestAvailableForSeries: props.useBestAvailable,
                useProgressiveCreateYourOwn: props.useProgressiveCyo,
            });

            if (bookingUrl) {
                bookingState.customBooking = {
                    bookingLabel: get(props.bookingOverride, 'bookingLinkLabel', 'Buy Tickets') ?? 'Buy Tickets',
                    bookingUrl,
                }

                bookingState.state = buttonStates.custom;
            }
        }

        state.selectedInstance = {
            bookingState,
            startDate,
            performanceId: props.performanceId,
        }
    }

    const sticky = document.querySelector('.event-header-sticky');

    if (sticky) {
        stickyHeaderObserver = new MutationObserver(() => {
            const wasOpen = packageDialog?.open;

            if (wasOpen) {
                packageDialog.close();
            }

            state.inOverlay = sticky.classList.contains('is-visible');

            if (wasOpen) {
                nextTick(() => {
                    packageDialog.showModal()
                });
            }
        });

        stickyHeaderObserver.observe(sticky, {
            attributes: true,
            attributeFilter: ['class'],
        });

        state.inOverlay = sticky.classList.contains('is-visible');
    }

    const mediaQuery = window.matchMedia('(min-width: 50em)');

    state.isDesktop = mediaQuery.matches;

    mediaQuery.addEventListener('change', event => {
        state.isDesktop = event.matches;
    });

    state.isLoading = false;
});

onBeforeUnmount(() => {
    if (stickyHeaderObserver) {
        stickyHeaderObserver.disconnect();
    }
});
</script>
